import { INNO_AVAILABLE_PROPERTIES } from "@/core/constants";
import { PRODUCT_VARIATIONS_LAYOUT_PROPERTY_NAME } from "@/shared/catalog/constants/product";
import { getProductRoute } from "../product";
import { getPropertiesGroupedByName } from "../properties";
import type {
  AnyLineItemType,
  VendorGroupType,
  VendorGroupByVendorIdType,
  PreparedLineItemType,
  PreparedRealtimeLineItemType,
} from "../../types";
import type {
  LineItemType,
  OrderLineItemType,
  RealtimeOrderItemOrderLineType,
  MoneyType,
  Product,
} from "@/core/api/graphql/types";

export function groupByVendor<T extends LineItemType | OrderLineItemType>(items: T[]): VendorGroupType<T>[] {
  // NOTE: The group without the vendor should be displayed last.
  const groupWithoutVendor: VendorGroupType<T> = { items: [] };
  const map: VendorGroupByVendorIdType<T> = {};

  items.forEach((item) => {
    const vendor = item.vendor;

    if (vendor) {
      const vendorId = vendor.id;

      map[vendorId] = map[vendorId] || { vendor, items: [] };
      map[vendorId].items.push(item);
    } else {
      groupWithoutVendor.items.push(item);
    }
  });

  const result = Object.values(map)
    // Sort by Vendor
    .sort((a, b) => a.vendor!.name.localeCompare(b.vendor!.name));

  // Add the group without the vendor to the end.
  result.push(groupWithoutVendor);

  return result;
}

function prepareItemPrices(item: AnyLineItemType) {
  const price = "price" in item ? item.price : undefined;
  const listPrice = price ?? ("listPrice" in item ? item.listPrice : undefined);
  const salePrice = "salePrice" in item ? item.salePrice : undefined;
  const placedPrice = "placedPrice" in item ? item.placedPrice : undefined;
  const extendedPrice = "extendedPrice" in item ? item.extendedPrice : undefined;
  const listTotal = "listTotal" in item ? item.listTotal : undefined;
  return {
    listPrice,
    salePrice,
    placedPrice,
    extendedPrice,
    actualPrice: placedPrice ?? salePrice ?? listPrice,
    listTotal,
  };
}

export function prepareLineItem(item: AnyLineItemType, countInCart?: number): PreparedLineItemType {
  const { listPrice, extendedPrice, actualPrice, listTotal } = prepareItemPrices(item);

  const productType = "productType" in item ? item.productType : undefined;
  const isVariation = !!item.product?.masterVariation;
  const quantity = item.selectedTierPrice?.quantity ?? item.quantity;
  const inStockQuantity =
    "inStockQuantity" in item ? item.inStockQuantity : item.product?.availabilityData?.availableQuantity;
  const properties = Object.values(getPropertiesGroupedByName(item.product?.properties ?? [])).filter((prop) =>
    INNO_AVAILABLE_PROPERTIES.includes(prop.name),
  );

  // Innovadis custom: route to varirant
  const route = getProductRoute(item.productId || item.product?.id || "", item.product?.slug);

  // const route = isVariation
  //   ? getProductRoute(item.product?.masterVariation?.id || "", item.product?.masterVariation?.slug)
  //   : getProductRoute(item.productId || item.product?.id || "", item.product?.slug);

  return {
    id: item.id,
    name: item.name || "",
    imageUrl: item.imageUrl,
    availabilityData: item.product?.availabilityData,
    productType,
    sku: item.sku,
    productId: item.productId,
    listPrice,
    actualPrice,
    extendedPrice,
    listTotal,
    quantity,
    inStockQuantity,
    route,
    deleted: !item.product,
    properties: properties.filter((property) => property.name !== PRODUCT_VARIATIONS_LAYOUT_PROPERTY_NAME).slice(0, 3),
    countInCart,
    minQuantity: item.product?.minQuantity,
    maxQuantity: item.product?.maxQuantity ?? item.inStockQuantity ?? item.product?.availabilityData?.availableQuantity,
    packSize: item.product?.packSize,
    isConfigurable: item.product?.isConfigurable,
    hasVariations: item.product?.hasVariations,
    variations: item.product?.variations,
    configurationItems: "configurationItems" in item ? item.configurationItems : undefined,
    showPlacedPrice: item.showPlacedPrice,
    product: item.product,
  };
}

export function prepareLineItems(items: AnyLineItemType[]): PreparedLineItemType[] {
  return items.map((item) => prepareLineItem(item));
}

// Innovadis custom
function prepareRealtimeItemPrices(item: RealtimeOrderItemOrderLineType) {
  const decimalDigits = 2;
  const currency = { code: "EUR", symbol: "€" };

  const formatMoney = (
    amount: number,
  ): {
    amount: number;
    currency: { code: string; symbol: string };
    formattedAmount: string;
    formattedAmountWithoutCurrency: string;
  } => ({
    amount,
    currency: currency,
    formattedAmount: amount.toFixed(decimalDigits) + `EUR`,
    formattedAmountWithoutCurrency: amount.toFixed(decimalDigits),
  });

  const actualPrice = item.orderedSalesQuantity !== 0 ? item.lineAmount! / item.orderedSalesQuantity! : 0;
  const listPrice = item.salesPrice!;
  const totalNetPrice = item.lineAmount!;

  return {
    listPrice: formatMoney(listPrice) as MoneyType,
    actualPrice: formatMoney(actualPrice) as MoneyType,
    extendedPrice: formatMoney(totalNetPrice) as MoneyType,
  };
}

// Innovadis custom
export function prepareRealtimeLineItem(item: RealtimeOrderItemOrderLineType): PreparedRealtimeLineItemType {
  const { listPrice, actualPrice, extendedPrice } = prepareRealtimeItemPrices(item);

  const quantity = item.orderedSalesQuantity;
  const netPrice = item.lineAmount! / item.orderedSalesQuantity!;
  const grossPrice = item.salesPrice!;
  const lineDiscountPercentage = item.lineDiscountPercentage!;
  const amount = item.orderedSalesQuantity!;
  const totalNetPrice = item.lineAmount!;

  return {
    id: item.itemNumber!,
    name: item.lineDescription || "",
    sku: item.itemNumber,
    amount,
    netPrice,
    totalNetPrice,
    quantity,
    grossPrice,
    extendedPrice,
    listPrice,
    actualPrice,
    lineDiscountPercentage,
    showPlacedPrice: true,
    status: item.salesOrderLineStatus,
    confirmedReceiptDate: item.confirmedReceiptDate as Date,
  };
}

// Innovadis custom
export function prepareRealtimeLineItems(items: RealtimeOrderItemOrderLineType[]): PreparedRealtimeLineItemType[] {
  return items.map((item) => prepareRealtimeLineItem(item));
}

export function prepareLineItemForProduct(item: Product, countInCart?: number): PreparedLineItemType {
  const tempLineItem: AnyLineItemType = {
    id: item.id,
    name: item.name,
    imageUrl: item.imgSrc,
    product: item,
    productId: item.id,
    sku: item.code,
    quantity: 0,
    inStockQuantity: item.availabilityData?.availableQuantity,
    productType: item.productType,
    price: item.price?.actual,
  };

  return prepareLineItem(tempLineItem, countInCart);
}
